import type { Feature, FeatureCollection } from "geojson";
import { Geo } from "@rogoag/geo";
import { polygonPartLabel } from "@domain/Geo/geometry";
import type { Deal } from "@domain/CRM/deal";

/** The source pre-sampling-processor files these problems under in a job's Software Errors. */
export const GEO_VALIDATOR_SOURCE = "Geo-Spatial Validator";

/**
 * The label the map puts on each boundary polygon, in the order the geo validator numbers them.
 *
 * The validator flattens every feature into single polygons and then drops the invalid ones, so
 * its "part 3" counts only the parts that survived that filter — the same filter is applied here
 * so the numbers line up. The result is indexed by the validator's part number minus one.
 */
function boundaryPartLabels(boundary: FeatureCollection): string[] {
	const labels: string[] = [];
	boundary.features.forEach((feature, featureIndex) => {
		const geometry = feature.geometry;
		const parts =
			geometry.type === "Polygon"
				? [geometry.coordinates]
				: geometry.type === "MultiPolygon"
					? geometry.coordinates
					: [];
		const isMulti = geometry.type === "MultiPolygon";
		parts.forEach((coordinates, pathIndex) => {
			const part = { type: "Feature", properties: {}, geometry: { type: "Polygon", coordinates } } as Feature;
			if (!Geo.isPolygonValid(part as Parameters<typeof Geo.isPolygonValid>[0]).isValid) return;
			labels.push(polygonPartLabel(featureIndex, pathIndex, isMulti));
		});
	});
	return labels;
}

/**
 * A boundary finding's wording, with the polygons it is about named the way the map names them.
 *
 * "Boundary contains a duplicate polygon" says nothing about which two of forty polygons are the
 * pair, and the validator already knows — it just reports the part numbers in `data` rather than
 * in the sentence. Naming them turns the message into something a person can act on.
 */
function describeBoundaryFinding(
	finding: { message: string; data: Record<string, unknown> },
	labels: string[],
): string {
	const parts = finding.data.duplicate_polygon_parts;
	if (!Array.isArray(parts) || parts.length !== 2) return finding.message;
	const named = parts.map((part) => (typeof part === "number" ? labels[part - 1] : undefined));
	if (named.some((label) => !label)) return finding.message;
	return `${finding.message} (${named[0]} and ${named[1]})`;
}

/** A job's loaded geometry, one FeatureCollection per layer (absent when the job has no such file). */
export interface JobGeometry {
	boundary?: FeatureCollection | null;
	points?: FeatureCollection | null;
	zones?: FeatureCollection | null;
}

type DealRules = Pick<Deal, "sitesType" | "density" | "isPtsRequired" | "syncMethod">;

/** Whether a job must carry sample points: pre-sampling-processor's `JobExtended.is_pts_required`. Agrian may not
 *  give us points; otherwise the deal requires them, or it is an immutable-sync Grid deal, or a ModGrid deal.
 *
 *  NOTE this is only half of the gate the geo validator applies. Its `validate_points` asks
 *  `deal.sites_type == "Grid" and job.is_pts_required()`, so a ModGrid deal answers true here and
 *  is still not point-validated. Callers mirroring the lambda must apply the Grid test too — see
 *  `validateJobGeometry`. The two halves are kept apart because this predicate is the lambda's,
 *  and changing it to fold the Grid test in would make it disagree with its counterpart. */
export function arePointsRequired(deal: DealRules, jobFlags: readonly string[]): boolean {
	if (jobFlags.includes("Agrian")) return false;
	return (
		deal.isPtsRequired ||
		(deal.sitesType === "Grid" && (deal.syncMethod ?? "").toLowerCase().includes("immut")) ||
		deal.sitesType === "ModGrid"
	);
}

/**
 * The problems pre-sampling-processor's `validate_geo_data` would file for this geometry, in its order and wording:
 * the boundary, then the points (Grid jobs that require them), then the zones (Zone jobs). The geometry rules
 * themselves are `@rogoag/geo`'s, shared with that lambda. Empty when the geometry is valid.
 */
export function validateJobGeometry(
	geometry: JobGeometry,
	deal: DealRules | null,
	jobFlags: readonly string[],
): string[] {
	const { boundary, points, zones } = geometry;
	if (!boundary) return ["No boundary shapefile attached"];
	const partLabels = boundaryPartLabels(boundary);
	const problems = Geo.validateBoundary(boundary).map((f) => describeBoundaryFinding(f, partLabels));

	// Points: the lambda asks for the deal before anything else, so a job without one says so here.
	if (!deal) return [...problems, "No deal associated with job"];
	// Both halves, deliberately: validate_points returns early unless
	// `deal.sites_type == "Grid" and job.is_pts_required()`. Dropping the Grid test would make this
	// report problems on ModGrid jobs that the lambda never files — a dialog blocking a save over an
	// error the Geo tab could not then clear.
	if (deal.sitesType === "Grid" && arePointsRequired(deal, jobFlags)) {
		if (!deal.density) problems.push("No density specified for deal");
		else if (!points) problems.push("No points shapefile attached");
		else problems.push(...Geo.validatePoints(boundary, points, deal.density).map((f) => f.message));
	}

	if (deal.sitesType === "Zone") {
		if (!deal.density) problems.push("No density specified for deal");
		else if (!zones) problems.push("No zones shapefile attached");
		else problems.push(...Geo.validateZones(boundary, zones, deal.density).map((f) => f.message));
	}
	return problems;
}
